import { Notification } from '@thanx/uikit/notification'
import { Switch } from '@thanx/uikit/switch'
import useFlag from 'hooks/useFlag'
import useProducts from 'hooks/useProducts'
import { buildTranslate } from 'locales'
import isEmpty from 'lodash/isEmpty'
import { useEffect, useMemo, useState } from 'react'
import { I18n } from 'react-redux-i18n'
import ModifierToggle from 'scenes/RedeemManager/Builder/ConfigurationStep/ModifierToggle'
import { type DiscountScope } from 'scenes/RedeemManager/Builder/FormModel'
import {
  getDiscountedModifiersText,
  getDiscountedProductsText,
  getEffectiveDiscountScope,
  getFreeItemRestrictionNoteKey,
  getFreeItemsNoteKey,
  getZeroPricedBaseItems,
  getZeroPricedItemsWithoutModifiers,
  isFixedPriceKey,
  isFreeVariantKey,
  isModifierDiscountScope,
} from 'scenes/RedeemManager/helpers'
import { RedeemTypeItem } from 'scenes/RedeemManager/types'
import ModifierSelect from './ModifierSelect'
import ProductSelect from './ProductSelect'

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.online_redemption_card'
)
const wrapperT = buildTranslate('redeem_manager.modifier_select.wrapper')

type NotPurchaseProps = {
  discountedProductIds: string[]
  discountedModifierIds: string[]
  modifierDiscountIds?: string[]
  modifierDiscountsAdvanced?: boolean
  onModifierDiscountsAdvancedChange?: (advanced: boolean) => void
  requiredProductIds: string[]
  typeItem: RedeemTypeItem | null
  discountScope?: DiscountScope
}

const NotPurchase = ({
  discountedProductIds,
  discountedModifierIds,
  modifierDiscountIds = [],
  modifierDiscountsAdvanced = false,
  onModifierDiscountsAdvancedChange,
  requiredProductIds,
  typeItem,
  discountScope,
}: NotPurchaseProps) => {
  const key = typeItem?.key
  const isModifierRequired = key === 'any_modifier_for_$x'
  const hasDiscountedModifierIds = Boolean(
    Array.isArray(discountedModifierIds) && discountedModifierIds.length > 0
  )
  const hasDiscountedProductIds = Boolean(
    Array.isArray(discountedProductIds) && discountedProductIds.length > 0
  )
  const effectiveScope = getEffectiveDiscountScope(discountScope)
  const { products } = useProducts()
  const { zeroPricedBaseItems, zeroPricedProductIds } = useMemo(() => {
    const items = getZeroPricedBaseItems(
      discountScope,
      key,
      discountedProductIds,
      products
    )
    return {
      zeroPricedBaseItems: items,
      zeroPricedProductIds: new Set(items.map(item => item.chain_uid)),
    }
  }, [discountScope, key, discountedProductIds, products])
  const zeroPricedItemsWithoutModifiers = useMemo(
    () =>
      getZeroPricedItemsWithoutModifiers(
        discountScope,
        key,
        discountedProductIds,
        discountedModifierIds,
        products
      ),
    [discountScope, key, discountedProductIds, discountedModifierIds, products]
  )
  const showNoModifiersWarning = zeroPricedItemsWithoutModifiers.length > 0
  const modifierScopeSelected =
    effectiveScope === 'base_item_plus_all_modifiers' ||
    effectiveScope === 'base_item_plus_required_modifiers' ||
    effectiveScope === 'required_modifiers'
  const [isModifierToggleVisible, setIsModifierToggleVisible] = useState(
    isModifierRequired || hasDiscountedModifierIds || modifierScopeSelected
  )
  // The warning tells the merchant to select a modifier below, so make sure
  // the picker is open even when the scope changed after mount.
  useEffect(() => {
    if (showNoModifiersWarning) setIsModifierToggleVisible(true)
  }, [showNoModifiersWarning])
  // Saved restrictions are always enforced, so keep them visible when a scope
  // change moves them back behind the toggle.
  useEffect(() => {
    if (hasDiscountedModifierIds) setIsModifierToggleVisible(true)
  }, [hasDiscountedModifierIds])
  const subtype = typeItem?.subtype
  const itemOrBogoItem = subtype === 'item' || subtype === 'bogo/item'
  const displayDiscountedProducts =
    itemOrBogoItem || (subtype === null && !isEmpty(discountedProductIds))
  const freeModifierRewardsEnabled = useFlag('mx-free-modifier-rewards', false)
  const isFixedPriceType = isFixedPriceKey(key)
  const displayModifierDiscounts =
    isFixedPriceType && isModifierDiscountScope(discountScope)
  const setModifierDiscountsAdvanced = (advanced: boolean) =>
    onModifierDiscountsAdvancedChange?.(advanced)
  const displayDiscountedModifiers =
    (freeModifierRewardsEnabled &&
      (key === 'get_a_free_item' || key === 'get_a_free_modifier')) ||
    isFixedPriceType
  const displayRequiredProducts =
    subtype === 'bogo/item' ||
    subtype === 'bogo/purchase' ||
    (subtype === null && !isEmpty(requiredProductIds))
  const isFreeItem =
    typeItem?.types.includes('manual/item') || isFreeVariantKey(key)
  const isFreeModifier = subtype?.includes('modifier') ?? false
  const showModifierRestrictionWarning =
    (key === 'get_a_free_item' || isFreeVariantKey(key)) &&
    (subtype === 'item' || subtype === null) &&
    hasDiscountedProductIds &&
    hasDiscountedModifierIds
  const popoverVariant = key === 'get_a_free_item' ? 'freeItem' : 'default'
  const {
    label: modifiersLabel,
    helpText: defaultModifiersHelpText,
    secondaryHelpText: modifiersSecondaryHelpText,
  } = getDiscountedModifiersText(subtype)

  const freeRestrictionNoteKey = getFreeItemRestrictionNoteKey(
    key,
    discountScope
  )
  const freeItemsNoteKey = getFreeItemsNoteKey(key, discountScope)

  const getModifierLabel = () => {
    if (freeRestrictionNoteKey) return t('free_restriction_title')
    if (!isFixedPriceType) return modifiersLabel

    switch (effectiveScope) {
      case 'base_item_plus_all_modifiers':
      case 'base_item_plus_required_modifiers':
      case 'required_modifiers':
        return t('discounted_fixed_price_modifier_title')
      default:
        return modifiersLabel
    }
  }

  const getModifierHelpText = () => {
    if (freeRestrictionNoteKey) return t('free_restriction_help_text')
    if (!isFixedPriceType) return defaultModifiersHelpText

    switch (effectiveScope) {
      case 'base_item':
        return t('discounted_label_modifier_base_item')
      case 'base_item_plus_all_modifiers':
        return t('discounted_label_modifier_all_modifiers')
      case 'base_item_plus_required_modifiers':
      case 'required_modifiers':
        return t('discounted_label_modifier_required_modifiers')
      default:
        return defaultModifiersHelpText
    }
  }
  const modifiersHelpText = getModifierHelpText()

  return (
    <>
      <Switch condition={displayRequiredProducts}>
        <ProductSelect
          className="mb-m"
          name="onlineRequiredProductIds"
          label={t('required_label')}
          fallbackLabel={t('required_label_fallback')}
          isFreeItem={isFreeItem}
          value={requiredProductIds}
          validations={{ isRequired: true }}
          validationErrors={{
            isRequired: I18n.t('validations.is_required'),
          }}
        />
      </Switch>
      <Switch condition={displayDiscountedProducts}>
        <>
          <ProductSelect
            label={getDiscountedProductsText(isFreeItem, subtype).label}
            fallbackLabel={t(
              isFreeItem
                ? 'discounted_label_free_item_fallback'
                : 'discounted_label_fallback'
            )}
            name="onlineDiscountedProductIds"
            isFreeItem={isFreeItem}
            value={discountedProductIds}
            validations={{ isRequired: true }}
            validationErrors={{
              isRequired: I18n.t('validations.is_required'),
            }}
            zeroPricedProductIds={zeroPricedProductIds}
          />
          <p className="mt-xs body-text-4 grey-70">
            {freeItemsNoteKey
              ? t(freeItemsNoteKey)
              : isFreeItem
              ? t('will_apply_lowest_free_item')
              : t('will_apply_lowest')}
          </p>
          <Switch condition={displayDiscountedModifiers && !isFixedPriceType}>
            <p className="mt-xs body-text-4 grey-70">{t('including_addons')}</p>
          </Switch>
        </>
      </Switch>
      <div aria-live="polite" aria-atomic="true">
        <Switch condition={showModifierRestrictionWarning}>
          <Notification kind="warning" size="full">
            {t('modifier_restriction_warning')}
          </Notification>
        </Switch>
        <Switch condition={zeroPricedBaseItems.length > 0}>
          <Notification kind="warning" size="full">
            <p className="mb-xs">{t('base_item_zero_price_warning.heading')}</p>
            <ul className="mb-xs pl-s body-text-4">
              {zeroPricedBaseItems.map(item => (
                <li key={item.chain_uid}>{item.name}</li>
              ))}
            </ul>
            <p className="mb-0">
              {t('base_item_zero_price_warning.body_before_link')}
              <a
                href="/rewards/create"
                target="_blank"
                rel="noopener noreferrer"
                className="font-weight-bold"
              >
                {t('base_item_zero_price_warning.link_text')}
                <span className="sr-only">
                  {t('base_item_zero_price_warning.link_new_tab_hint')}
                </span>
              </a>
              {t('base_item_zero_price_warning.body_after_link')}
            </p>
          </Notification>
        </Switch>
        <Switch condition={showNoModifiersWarning}>
          <Notification kind="warning" size="full">
            <p className="mb-xs">
              {t('no_modifiers_zero_price_warning.heading')}
            </p>
            <ul className="mb-xs pl-s body-text-4">
              {zeroPricedItemsWithoutModifiers.map(item => (
                <li key={item.chain_uid}>{item.name}</li>
              ))}
            </ul>
            <p className="mb-0">{t('no_modifiers_zero_price_warning.body')}</p>
          </Notification>
        </Switch>
      </div>
      <Switch condition={displayDiscountedModifiers}>
        {isModifierRequired ? (
          <ModifierSelect
            productIds={discountedProductIds}
            label={getModifierLabel()}
            helpText={modifiersHelpText}
            secondaryHelpText={modifiersSecondaryHelpText}
            shouldDisplayPopover={!isFreeModifier}
            popoverVariant={popoverVariant}
            name="onlineDiscountedModifierIds"
            value={discountedModifierIds}
          />
        ) : displayModifierDiscounts ? (
          // Restrictions are always shown under modifier discounts; outside
          // advanced mode they double as the discounted modifiers
          <>
            <ModifierSelect
              productIds={discountedProductIds}
              label={t(
                modifierDiscountsAdvanced
                  ? 'modifier_discounts_required_title'
                  : 'modifier_discounts_simple_title'
              )}
              helpText={t(
                modifierDiscountsAdvanced
                  ? 'modifier_discounts_required_help_text'
                  : 'modifier_discounts_simple_help_text'
              )}
              shouldDisplayPopover={!isFreeModifier}
              popoverVariant={popoverVariant}
              name="onlineDiscountedModifierIds"
              value={discountedModifierIds}
            />
            <Switch condition={!modifierDiscountsAdvanced}>
              <p className="mt-xs body-text-4 grey-70">
                {t('modifier_discounts_cheapest_note')}
              </p>
            </Switch>
          </>
        ) : (
          <ModifierToggle
            isVisible={isModifierToggleVisible}
            setIsVisible={setIsModifierToggleVisible}
            contentId="online-modifier-restrictions"
            isButtonDisabled={!hasDiscountedProductIds}
          >
            <ModifierSelect
              productIds={discountedProductIds}
              label={getModifierLabel()}
              helpText={modifiersHelpText}
              secondaryHelpText={modifiersSecondaryHelpText}
              shouldDisplayPopover={!isFreeModifier}
              popoverVariant={
                key === 'get_a_free_item' ? 'freeItem' : 'default'
              }
              name="onlineDiscountedModifierIds"
              value={discountedModifierIds}
              toggleVisibility={setIsModifierToggleVisible}
              hideLabel={
                freeRestrictionNoteKey
                  ? t('free_restriction_disable')
                  : undefined
              }
            />
            <Switch condition={Boolean(freeRestrictionNoteKey)}>
              <p className="mt-xs body-text-4 grey-70">
                {freeRestrictionNoteKey && t(freeRestrictionNoteKey)}
              </p>
            </Switch>
          </ModifierToggle>
        )}
      </Switch>
      <Switch condition={displayModifierDiscounts}>
        <ModifierToggle
          isVisible={modifierDiscountsAdvanced}
          contentId="online-modifier-discounts"
          setIsVisible={setModifierDiscountsAdvanced}
          isButtonDisabled={!hasDiscountedProductIds}
          buttonLabel={wrapperT('display_modifier_discounts')}
        >
          <ModifierSelect
            productIds={discountedProductIds}
            label={t('modifier_discounts_title')}
            helpText={t('modifier_discounts_help_text')}
            name="onlineModifierDiscountIds"
            value={modifierDiscountIds}
            onHide={() => setModifierDiscountsAdvanced(false)}
            hideLabel={wrapperT('hide_modifier_discounts')}
          />
        </ModifierToggle>
      </Switch>
    </>
  )
}

export default NotPurchase
